import React, { useState } from 'react';

import { Search, Loader2, Filter } from 'lucide-react';

import FactFiltersPopup from '@/screens/Chronos/CaseEditor/DataView/FactsEditor/FactsEditorToolbar/components/FactFilters';
import useFactsData from '@/api/queries/useGetFacts';
import { useGetCase } from '@/api/queries/useGetCase';
import { useGetCaseDocThemes } from '@/api/queries/useGetCaseDocThemes';
import { useGetCaseDocTypes } from '@/api/queries/useGetCaseDocTypes';
import CustomModal from '@/components/molecules/Modals/CustomModal';
import FactDropdown from '@/components/organisms/FactDropdown';
import type { FactFilters } from '@/types';

interface FactBrowserProps {
  caseId: string;
  onSelectFact: (factId: string) => void;
}

const FactBrowser: React.FC<FactBrowserProps> = ({ caseId, onSelectFact }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FactFilters>({});
  const [showFilters, setShowFilters] = useState(false);

  const { data: factsData, isLoading } = useFactsData({
    caseId,
    docId: null,
    searchQuery: searchQuery || undefined,
    filters,
    page: 1,
    pageSize: 50,
  });

  // Fetch data for filters
  const { data: caseData } = useGetCase(caseId);
  const { data: themesData } = useGetCaseDocThemes({ caseId });
  const { data: docTypesData } = useGetCaseDocTypes({ caseId });

  const caseRuns = caseData?.runs || [];
  const caseThemes = themesData?.themes || [];
  const allDocumentTypes = docTypesData?.documentTypes || [];
  const allPersons = caseData?.persons || [];

  const facts = factsData?.facts || [];

  // Count active filters
  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'searchQuery') return false; // Don't count search query
    if (Array.isArray(value)) return value.length > 0;
    if (value === null || value === undefined) return false;
    return true;
  }).length;

  return (
    <div className="flex h-full flex-col">
      {/* Search Input with Filter Button */}
      <div className="mb-3 flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search facts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-md border border-gray-300 py-2 pr-3 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
          />
        </div>
        <button
          onClick={() => setShowFilters(true)}
          className="relative flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
        >
          <Filter className="size-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-1 flex size-5 items-center justify-center rounded-full bg-blue-600 text-xs text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Facts List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="size-6 animate-spin text-gray-400" />
          </div>
        ) : facts.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">
            {searchQuery ? 'No facts found matching your search.' : 'No facts available.'}
          </div>
        ) : (
          <div className="space-y-2">
            {facts.map((fact) => (
              <div
                key={fact.id}
                onClick={() => onSelectFact(fact.id)}
                className="cursor-pointer rounded-lg border border-gray-200 transition-colors hover:border-blue-500"
              >
                <FactDropdown fact={fact} compressedVersion />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filter Modal */}
      <CustomModal
        isOpen={showFilters}
        onRequestClose={() => setShowFilters(false)}
        content={
          <FactFiltersPopup
            allDocumentTypes={allDocumentTypes}
            allPersons={allPersons}
            activeFilters={filters}
            setFilters={setFilters}
            handleClose={() => setShowFilters(false)}
            caseRuns={caseRuns}
            caseThemes={caseThemes}
          />
        }
      />
    </div>
  );
};

export default FactBrowser;
